import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@guryou/colors';
import { Form } from '@guryou/html-components';
import { createReservationAsAnonymous, getPersonnelAvailabilityDates, getPersonnelAvailabilitySlots, useEffectOnSuccess } from '@guryou/redux';
import { printInputDate, printPrice, printTime } from '@guryou/utils';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { WidgetContext } from './WidgetContext';

const AccordionDetailsService = ({ subservice, dispatch, localData, setLocalData, personnelTimeSlots, personnelDateSlots, closeModal }) => {
  const { t } = useTranslation();
  const [showHours, setShowHours] = useState(false);
  const { askFirstname, askLastname, askPhone, paymentOptions, user, fontF, fontS } = useContext(WidgetContext);
  const { setActiveServiceId } = useContext(WidgetContext);

  const serviceOnline = paymentOptions.data.serviceOnline;
  const serviceOnSite = paymentOptions.data.serviceOnsite;
  const [userInfo, setUserInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    surname: user?.lastname || '',
    phone: user?.phone || '',
  });

  const isFormValid = () => {
    if (!userInfo?.email && !user?.email) {
      return false;
    }
    if (askFirstname && !userInfo.firstname) {
      return false;
    }
    if (askLastname && !userInfo.lastname) {
      return false;
    }
    if (askPhone && !userInfo.phone) {
      return false;
    }
    return true;
  };

  const getInputToState = (inputField, value) => {
    const newUserInfo = userInfo; // create new state object

    userInfo[inputField] = value; // Change th value

    setUserInfo(newUserInfo); // Set new state
  };

  const reserveClick = onlyBook => {
    if (!isFormValid()) {
      alert(t('VALIDATIONS.missingFields'));
      return;
    }
    dispatch(
      createReservationAsAnonymous({
        subServiceId: subservice.id,
        dateTimeFrom: `${printInputDate(localData.date)}T${localData.time}`,
        email: userInfo.email ? userInfo.email : user.email,
        firstname: userInfo.name ? userInfo.name : user.name,
        phone: userInfo.phone ? userInfo.phone : user.phone,
        resourceId: localData.resourceId,
        lastname: userInfo.surname ? userInfo.surname : user.lastname,
        onlyBook,
      })
    );
  };

  useEffectOnSuccess([state => state.reservations.create], reserve => {
    setActiveServiceId(null);
    if (!reserve?.id) {
      alert(t('GENERAL.checkEmailToConfirm'));
    }
  });

  const updateLocalData = useCallback(() => {
    if (subservice.resources.length === 1) {
      const resourceId = Number(subservice.resources[0]?.id);
      setLocalData(prevData => ({ ...prevData, resourceId }));
      dispatch(getPersonnelAvailabilityDates(subservice.id, resourceId));
    }
  }, [dispatch, setLocalData, subservice]);

  useEffect(() => {
    updateLocalData();
  }, [updateLocalData]);

  return (
    <AccordionDetails id="serviceAccordian">
      <div class="flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-1 justify-center items-center w-full md:inset-0 max-h-full bg-black bg-opacity-50">
        <div class="relative p-4 w-full max-w-[720px] max-h-full">
          <div class="relative bg-white rounded-lg shadow ">
            <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
              <h3 class="text-lg font-semibold text-gray-900">{subservice?.name}</h3>
              <button
                onClick={() => closeModal()}
                type="button"
                class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                data-modal-toggle="crud-modal"
              >
                <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                </svg>
                <span class="sr-only">Close modal</span>
              </button>
            </div>
            <Form id="serviceForm" parserVersion={2} actions={false}>
              <div class="p-4 md:p-5">
                <ol class="relative text-gray-500 border-s border-gray-200 ml-4 pt-6">
                  <li class="mb-10 ms-6">
                    <span
                      class={`absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:bg-[${colors.mandy}]`}
                    >
                      {localData.resourceId && localData.time && showHours ? (
                        <svg
                          class="w-3.5 h-3.5 text-gray-800 dark:text-white"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 16 12"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M1 5.917 5.724 10.5 15 1.5"
                          />
                        </svg>
                      ) : (
                        <svg
                          class="w-3.5 h-3.5 text-gray-800 dark:text-white"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-2 9a4 4 0 0 0-4 4v1a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1a4 4 0 0 0-4-4H6Zm7.25-2.095c.478-.86.75-1.85.75-2.905a5.973 5.973 0 0 0-.75-2.906 4 4 0 1 1 0 5.811ZM15.466 20c.34-.588.535-1.271.535-2v-1a5.978 5.978 0 0 0-1.528-4H18a4 4 0 0 1 4 4v1a2 2 0 0 1-2 2h-4.535Z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      )}
                    </span>
                    <h3 class="font-medium leading-tight">{t('PROFILE.resource')}</h3>
                    <p class="text-sm">{t('SERVICES.staffStepDescription')}</p>
                    <div class="grid gap-4 mb-4 grid-cols-2 mt-6 w-full">
                      <div class="col-span-2">
                        <label for="category" class="block mb-2 text-sm font-medium text-gray-900">
                          {t('SERVICES.staff') + `*`}
                        </label>
                        <select
                          id="category"
                          class={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${colors.mandy}] block w-full p-2.5`}
                          onChange={value => {
                            const resourceId = Number(value.target.value);
                            setLocalData({ ...localData, resourceId: resourceId });
                            dispatch(getPersonnelAvailabilityDates(subservice.id, resourceId));
                          }}
                          defaultValue={(subservice.resources.length <= 1 && subservice.resources[0]?.id) || null}
                        >
                          <option value={0}>{t('PROFILE.resource')}</option>
                          {subservice.resources?.map(resource => (
                            <option value={resource.id}>{resource.name}</option>
                          ))}
                        </select>
                      </div>
                      <div class="col-span-2 sm:col-span-1">
                        <label for="name" class="block mb-2 text-sm font-medium text-gray-900">
                          {t('GENERAL.pleaseSelectDate') + `*`}
                        </label>
                        <input
                          type="date"
                          class={`${
                            localData.resourceId ? 'bg-gray-50' : 'bg-gray-300'
                          } border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${
                            colors.mandy
                          }] block w-full p-2.5`}
                          required=""
                          min={new Date().toISOString().split('T')[0]}
                          disabled={!localData.resourceId}
                          onChange={val => {
                            const date = `${val.target.value}T00:00:00.00`;
                            setLocalData({ ...localData, date: date });
                            dispatch(getPersonnelAvailabilitySlots(subservice.id, localData.resourceId, printInputDate(date)));
                            setShowHours(true);
                          }}
                        />
                      </div>
                      <div class="col-span-2 sm:col-span-1">
                        <label for="category" class="block mb-2 text-sm font-medium text-gray-900">
                          {t('RESERVATIONS.reservationTime') + `*`}
                        </label>
                        <select
                          id="category"
                          class={`${
                            showHours && localData.resourceId && personnelTimeSlots.data?.length ? 'bg-gray-50' : 'bg-gray-300'
                          } border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${
                            colors.mandy
                          }] block w-full p-2.5`}
                          onChange={value => {
                            const time = value.target.value;
                            setLocalData({ ...localData, time: time });
                          }}
                          disabled={!showHours || !localData.resourceId || !personnelTimeSlots.data?.length}
                        >
                          <option value={1}>{t('RESERVATIONS.reservationTime')}</option>
                          {personnelTimeSlots.data?.map(resource => {
                            const time = printTime(resource);
                            return <option value={resource}>{time}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                  </li>
                  <li class="mb-10 ms-6">
                    <span
                      class={`absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -start-4 ring-4 ring-white dark:bg-[${
                        localData.resourceId && localData.time && showHours ? colors.mandy : colors.gray
                      }]`}
                    >
                      <svg
                        class="w-3.5 h-3.5 text-gray-800 dark:text-white"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 16"
                      >
                        <path d="M18 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM6.5 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3.014 13.021l.157-.625A3.427 3.427 0 0 1 6.5 9.571a3.426 3.426 0 0 1 3.322 2.805l.159.622-6.967.023ZM16 12h-3a1 1 0 0 1 0-2h3a1 1 0 0 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Z" />
                      </svg>
                    </span>
                    <h3 class="font-medium leading-tight">{t('SERVICES.personalData')}</h3>
                    <p class="text-sm">{t('SERVICES.personalDataStepDescription')}</p>
                    <div class="grid gap-4 mb-4 grid-cols-2 mt-6">
                      <div class="col-span-2 sm:col-span-1">
                        <label for="name" class="block mb-2 text-sm font-medium text-gray-900">
                          {askFirstname ? t('GENERAL.name') + '*' : t('GENERAL.name')}
                        </label>
                        <input
                          type="text"
                          placeholder={askFirstname ? t('GENERAL.name') + '*' : t('GENERAL.name')}
                          name="name"
                          class={`${
                            localData.resourceId && localData.time && showHours ? 'bg-gray-50' : 'bg-gray-300'
                          } border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${
                            colors.mandy
                          }] block w-full p-2.5`}
                          disabled={localData.resourceId && localData.time && showHours ? false : true}
                          onChange={name => getInputToState('name', name.target.value)}
                          value={user ? user.firstname : null}
                          required={askFirstname}
                        />
                      </div>
                      <div class="col-span-2 sm:col-span-1">
                        <label for="name" class="block mb-2 text-sm font-medium text-gray-900">
                          {askLastname ? t('GENERAL.surname') + '*' : t('GENERAL.surname')}
                        </label>
                        <input
                          type="text"
                          placeholder={askLastname ? t('GENERAL.surname') + '*' : t('GENERAL.surname')}
                          name="lastname"
                          class={`${
                            localData.resourceId && localData.time && showHours ? 'bg-gray-50' : 'bg-gray-300'
                          } border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${
                            colors.mandy
                          }] block w-full p-2.5`}
                          disabled={localData.resourceId && localData.time && showHours ? false : true}
                          onChange={surname => getInputToState('surname', surname.target.value)}
                          value={user ? user.lastname : null}
                          required={askLastname}
                        />
                      </div>
                      <div class="col-span-2 sm:col-span-1">
                        <label for="name" class="block mb-2 text-sm font-medium text-gray-900">
                          {t('GENERAL.email') + '*'}
                        </label>
                        <input
                          type="text"
                          placeholder={t('GENERAL.email') + '*'}
                          name="email"
                          id="email"
                          class={`${
                            localData.resourceId && localData.time && showHours ? 'bg-gray-50' : 'bg-gray-300'
                          } border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${
                            colors.mandy
                          }] block w-full p-2.5`}
                          disabled={localData.resourceId && localData.time && showHours ? false : true}
                          onChange={email => getInputToState('email', email.target.value)}
                          value={user ? user.email : null}
                          required={true}
                        />
                      </div>
                      <div class="col-span-2 sm:col-span-1">
                        <label for="name" class="block mb-2 text-sm font-medium text-gray-900">
                          {askPhone ? t('GENERAL.phone') + '*' : t('GENERAL.phone')}
                        </label>
                        <input
                          type="text"
                          placeholder={askPhone ? t('GENERAL.phone') + '*' : t('GENERAL.phone')}
                          name="phone"
                          class={`${
                            localData.resourceId && localData.time && showHours ? 'bg-gray-50' : 'bg-gray-300'
                          } border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[${colors.mandy}] focus:border-[${
                            colors.mandy
                          }] block w-full p-2.5`}
                          disabled={localData.resourceId && localData.time && showHours ? false : true}
                          onChange={phone => getInputToState('phone', phone.target.value)}
                          value={user ? user.phone : null}
                          required={askPhone}
                        />
                      </div>
                    </div>
                  </li>
                </ol>

                <div class="flex items-end pt-6 space-x-2 justify-end">
                  {serviceOnSite && (
                    <button
                      class={`text-white inline-flex items-center disabled:bg-gray-300 bg-[${colors.mandy}] hover:opacity-90 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center`}
                      disabled={localData.resourceId && localData.time && showHours ? false : true}
                      onClick={() => reserveClick(true)}
                      type="button"
                    >
                      {`${t('NEWSLETTER.subscribe')} ${printPrice(subservice.price)} `}
                    </button>
                  )}
                  {serviceOnline && (
                    <button
                      class={`text-white inline-flex items-center bg-[${colors.mandy}] disabled:bg-gray-300 hover:opacity-90 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center`}
                      disabled={localData.resourceId && localData.time && showHours ? false : true}
                      onClick={() => reserveClick(false)}
                      type="button"
                    >
                      {`${t('GENERAL.payNow')} ${printPrice(subservice.price)} `}
                    </button>
                  )}
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </AccordionDetails>
  );
};

export default AccordionDetailsService;
